import React from 'react';
import { Box, ButtonBase, Paper, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { utCodesFor, rcCodes, otherSpeaker } from './codes';

// One code in the panel (Figma "Codebook entry"); highlighted = a code in play
export const CodebookEntry = ({ code, name, definition, highlighted = false }) => (
    <Box
        component="li"
        sx={{
            display: 'grid',
            gridTemplateColumns: '36px 1fr',
            gap: 1.5,
            p: 1,
            borderRadius: 1,
            listStyle: 'none',
            bgcolor: highlighted ? (theme) => alpha(theme.palette.primary.main, 0.08) : 'transparent',
            boxShadow: highlighted ? (theme) => `inset 3px 0 0 ${theme.palette.primary.main}` : 'none',
            transition: (theme) => theme.transitions.create(['background-color', 'box-shadow'], { duration: theme.transitions.duration.short }),
        }}
    >
        <Typography component="span" variant="body2" sx={{ fontWeight: 700, color: highlighted ? 'primary.main' : 'text.primary' }}>
            {code}
        </Typography>
        <Box sx={{ minWidth: 0 }}>
            <Typography component="span" variant="body2" sx={{ fontWeight: 600, display: 'block' }}>{name}</Typography>
            <Typography component="span" variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                {definition}
            </Typography>
        </Box>
    </Box>
);

const TabButton = ({ selected, children, ...props }) => (
    <ButtonBase
        role="tab"
        aria-selected={selected}
        sx={{
            flex: 1,
            py: 0.75,
            borderRadius: 1,
            fontSize: '0.8125rem',
            fontWeight: selected ? 600 : 500,
            color: selected ? 'text.primary' : 'text.secondary',
            bgcolor: selected ? 'background.paper' : 'transparent',
            boxShadow: selected ? '0 1px 2px rgba(11, 18, 32, 0.08)' : 'none',
            transition: (theme) => theme.transitions.create(['background-color', 'color', 'box-shadow'], { duration: theme.transitions.duration.shortest }),
            '&.Mui-focusVisible': { outline: '2px solid', outlineColor: 'primary.light' },
        }}
        {...props}
    >
        {children}
    </ButtonBase>
);

const UT_CAPTIONS = {
    teacher: 'Teacher codes (the selected utterance is a teacher’s)',
    student: 'Student codes (the selected utterance is a student’s)',
    unknown: 'All codes (the speaker is unknown)',
};

// The codebook beside every coding screen, in the run's codebook version (Figma "Codebook panel").
// UT lists the codes for the selected utterance's speaker, RC the 5 classes. Everything comes from
// the `codebook` the API returned; `highlight` marks the codes in play per dimension.
// `inline`: the wide panel under the context on the item screen, with an overline header and the
// entries in two columns (wf-adj-item-blind-1)
const CodebookPanel = ({ codebook, speaker = 'unknown', tab, onTabChange, highlight = {}, inline = false, sx }) => {
    const [ownTab, setOwnTab] = React.useState('ut');
    const current = tab || ownTab;
    const setTab = onTabChange || setOwnTab;
    const id = React.useId();

    const entries = current === 'ut' ? utCodesFor(codebook, speaker) : rcCodes(codebook);
    const marked = highlight[current] || [];
    const other = otherSpeaker(speaker);
    const otherCodes = other ? utCodesFor(codebook, other).map((e) => e.code) : [];

    return (
        <Paper component="aside" aria-label="Codebook" sx={{ p: 2, ...sx }}>
            {inline ? (
                <Typography variant="overline" component="h2" color="text.secondary">
                    Codebook · CDAT {codebook?.version}, run codebook version
                </Typography>
            ) : (
                <>
                    <Typography variant="h6" component="h2">Codebook</Typography>
                    <Typography variant="caption" color="text.secondary">
                        CDAT · codebook version {codebook?.version}
                    </Typography>
                </>
            )}

            <Box role="tablist" aria-label="Dimension" sx={{ display: 'flex', gap: 0.25, p: 0.25, mt: inline ? 1 : 1.5, borderRadius: 1.5, bgcolor: 'action.hover', maxWidth: inline ? 320 : 'none' }}>
                <TabButton id={`${id}-ut`} aria-controls={`${id}-panel`} selected={current === 'ut'} onClick={() => setTab('ut')}>
                    Utterance type (UT)
                </TabButton>
                <TabButton id={`${id}-rc`} aria-controls={`${id}-panel`} selected={current === 'rc'} onClick={() => setTab('rc')}>
                    Reasoning (RC)
                </TabButton>
            </Box>

            <Box id={`${id}-panel`} role="tabpanel" aria-labelledby={`${id}-${current}`}>
                <Typography variant="caption" component="p" color="text.secondary" sx={{ mt: 1.5, mb: 0.5 }}>
                    {current === 'ut' ? UT_CAPTIONS[speaker] || UT_CAPTIONS.unknown : 'Reasoning component, 5 classes (rc5)'}
                </Typography>
                <Box component="ul" sx={{ m: 0, p: 0, display: 'grid', gap: 0.25, columnGap: 3, gridTemplateColumns: inline ? { xs: '1fr', md: '1fr 1fr' } : '1fr' }}>
                    {entries.map((entry) => (
                        <CodebookEntry key={entry.code} {...entry} highlighted={marked.includes(entry.code)} />
                    ))}
                </Box>
                <Typography variant="caption" component="p" color="text.secondary" sx={{ mt: 1 }}>
                    {current === 'rc'
                        ? 'Same set for teacher and student utterances.'
                        : otherCodes.length > 0 && `${other === 'student' ? 'Student' : 'Teacher'} rows show ${otherCodes.join(', ')} instead.`}
                </Typography>
            </Box>
        </Paper>
    );
};

export default CodebookPanel;
